import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/card'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { Badge } from '@components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@components/ui/dialog'
import { Label } from '@components/ui/label'
import { Textarea } from '@components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { Switch } from '@components/ui/switch'
import { Slider } from '@components/ui/slider'
import { featureFlagService, type FeatureFlag } from '@services/feature-flags.service'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Flag, Plus, Edit, Trash2, ToggleLeft } from 'lucide-react'

interface FeatureFlagForm {
  key: string
  name: string
  description: string
  flag_type: 'BOOLEAN' | 'PERCENTAGE' | 'VARIANT'
  default_value: boolean
  rollout_percentage: number
  category: string
}

export function FeatureFlagsPage() {
  const queryClient = useQueryClient()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null)
  const [form, setForm] = useState<FeatureFlagForm>({
    key: '',
    name: '',
    description: '',
    flag_type: 'BOOLEAN',
    default_value: false,
    rollout_percentage: 100,
    category: '',
  })

  const { data: flagsData } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => featureFlagService.getFeatureFlags({ limit: 100 }),
  })

  const createFlagMutation = useMutation({
    mutationFn: (data: { key: string; name: string; description?: string; flag_type: string; default_value: boolean; rollout_percentage?: number; category?: string }) => 
      featureFlagService.createFeatureFlag(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] })
      setIsDialogOpen(false)
      resetForm()
    },
  })

  const updateFlagMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<FeatureFlagForm> }) => 
      featureFlagService.updateFeatureFlag(id, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] })
      setIsDialogOpen(false)
      setSelectedFlag(null)
      resetForm()
    },
  })

  const deleteFlagMutation = useMutation({
    mutationFn: (id: string) => featureFlagService.deleteFeatureFlag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] })
      setIsDialogOpen(false)
      setSelectedFlag(null)
      resetForm()
    },
  })

  const resetForm = () => {
    setForm({
      key: '',
      name: '',
      description: '',
      flag_type: 'BOOLEAN',
      default_value: false,
      rollout_percentage: 100,
      category: '',
    })
  }

  const openEditDialog = (flag: FeatureFlag) => {
    setSelectedFlag(flag)
    setForm({
      key: flag.key,
      name: flag.name,
      description: flag.description || '',
      flag_type: flag.flag_type,
      default_value: flag.default_value,
      rollout_percentage: flag.rollout_percentage,
      category: flag.category || '',
    })
    setIsDialogOpen(true)
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Banderas de Funcionalidades</h1>
          <p className="text-muted-foreground">Gestionar lanzamientos y pruebas A/B</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open)
          if (!open) {
            setSelectedFlag(null)
            resetForm()
          }
        }}>
          <DialogTrigger asChild>
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Agregar Bandera
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{selectedFlag ? 'Editar' : 'Crear'} Bandera de Funcionalidad</DialogTitle>
              <DialogDescription>
                {selectedFlag ? 'Actualizar la' : 'Crear nueva'} configuración de bandera de funcionalidad.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {!selectedFlag && (
                <div className="grid gap-2">
                  <Label htmlFor="flag-key">Clave</Label>
                  <Input
                    id="flag-key"
                    value={form.key}
                    onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                    placeholder="my_feature_flag"
                  />
                </div>
              )}
              <div className="grid gap-2">
                <Label htmlFor="flag-name">Nombre</Label>
                <Input
                  id="flag-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="My Feature Flag"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="flag-desc">Descripción</Label>
                <Textarea
                  id="flag-desc"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Description of this feature flag"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="flag-type">Tipo</Label>
                <Select 
                  value={form.flag_type} 
                  onValueChange={(value: 'BOOLEAN' | 'PERCENTAGE' | 'VARIANT') => setForm({ ...form, flag_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BOOLEAN">Booleano</SelectItem>
                    <SelectItem value="PERCENTAGE">Despliegue Porcentual</SelectItem>
                    <SelectItem value="VARIANT">Variante</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="flag-category">Categoría</Label>
                <Input
                  id="flag-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="e.g., billing, ui, experimental"
                />
              </div>
              {form.flag_type === 'BOOLEAN' && (
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label>Valor Predeterminado</Label>
                    <p className="text-sm text-muted-foreground">Activar bandera por defecto</p>
                  </div>
                  <Switch
                    checked={form.default_value}
                    onCheckedChange={(checked) => setForm({ ...form, default_value: checked })}
                  />
                </div>
              )}
              {form.flag_type === 'PERCENTAGE' && (
                <div className="grid gap-2">
                  <Label>Porcentaje de Despliegue: {form.rollout_percentage}%</Label>
                  <Slider
                    value={[form.rollout_percentage]}
                    onValueChange={([value]) => setForm({ ...form, rollout_percentage: value })}
                    max={100}
                    step={5}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              {selectedFlag && (
                <Button 
                  variant="destructive" 
                  onClick={() => deleteFlagMutation.mutate(selectedFlag.id)}
                  className="mr-auto"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Eliminar
                </Button>
              )}
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button
                onClick={() => {
                  if (selectedFlag) {
                    const { key, ...updateData } = form
                    updateFlagMutation.mutate({ id: selectedFlag.id, data: updateData })
                  } else {
                    createFlagMutation.mutate(form)
                  }
                }}
              >
                {selectedFlag ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Banderas de Funcionalidades</CardTitle>
          <CardDescription>Gestionar disponibilidad y despliegue de funcionalidades</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bandera</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Valor Predeterminado</TableHead>
                <TableHead>Despliegue</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Sobreescrituras</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {flagsData?.items.map((flag: FeatureFlag) => (
                <TableRow key={flag.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Flag className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{flag.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{flag.key}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{flag.flag_type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={flag.default_value ? 'default' : 'secondary'}>
                      {flag.default_value ? 'ON' : 'OFF'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                      {flag.rollout_percentage}%
                    </div>
                  </TableCell>
                  <TableCell>
                    {flag.category ? (
                      <Badge variant="secondary">{flag.category}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={flag.is_active ? 'default' : 'outline'}>
                      {flag.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{flag.override_count}</Badge>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(flag)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!flagsData?.items.length && (
            <div className="text-center py-8 text-muted-foreground">
              Sin banderas de funcionalidades configuradas
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
